/**
 * How big the grids are drawn — chosen per device, separately for the counter grid and
 * the officer grid. The user steps through the levels with A− / A+ (see GridDensitySelect).
 * Every level lays a block out the same way: start and end time on a top line, the
 * name or counter below, and any note on a line of its own under the cell. Only the
 * scale changes:
 *
 *   fit    (100%) every row on screen at once where it can — rows never get shorter than
 *          two text lines, so a long roster scrolls rather than squeezing the text.
 *   smaller levels (70%, 85%) let rows drop below two lines (a block falls back to one
 *          line), to see more of a long roster at once.
 *   larger levels grow row height, text size and the narrowest column together, so
 *          text never outgrows the box it sits in (which is what a free-standing font
 *          slider would do on a layout whose sizes are set by fitting the screen). The
 *          grid then scrolls sideways, its row labels and time header staying put.
 */
import { useEffect, useState } from 'react';
import { loadPref, savePref } from './prefStorage';

export type GridDensity = 'xsmall' | 'small' | 'fit' | 'comfortable' | 'large' | 'xlarge';
export type GridView = 'counter' | 'officer';

export interface DensityPreset {
  label: string;
  /** Multiplier on the smallest height a row may be squeezed to. */
  rowFloor: number;
  /** Multiplier on text sizes (cell labels; officer row labels and header). */
  fontCap: number;
  /** Narrowest a 15-minute column may be, px — below it the grid scrolls sideways
   *  instead. 0 means fill the available width. */
  minCellWidth: number;
}

export const DENSITY_PRESETS: Record<GridDensity, DensityPreset> = {
  xsmall: { label: '70%', rowFloor: 0.7, fontCap: 0.7, minCellWidth: 0 },
  small: { label: '85%', rowFloor: 0.85, fontCap: 0.85, minCellWidth: 0 },
  fit: { label: '100%', rowFloor: 1, fontCap: 1, minCellWidth: 0 },
  comfortable: { label: '125%', rowFloor: 1.4, fontCap: 1.25, minCellWidth: 28 },
  large: { label: '150%', rowFloor: 1.8, fontCap: 1.5, minCellWidth: 52 },
  xlarge: { label: '180%', rowFloor: 2.2, fontCap: 1.8, minCellWidth: 70 },
};

/** The default level and what the reset button returns to: every row on screen where it can be. */
export const DEFAULT_DENSITY: GridDensity = 'fit';

export const GRID_DENSITIES = Object.keys(DENSITY_PRESETS) as GridDensity[];

/** The level one A− (-1) or A+ (+1) step away, stopping at either end. */
export function stepDensity(from: GridDensity, dir: -1 | 1): GridDensity {
  const i = GRID_DENSITIES.indexOf(from) + dir;
  return GRID_DENSITIES[Math.min(GRID_DENSITIES.length - 1, Math.max(0, i))];
}

const STORAGE_KEYS: Record<GridView, string> = {
  counter: 'ac_roster_grid_density',
  officer: 'ac_roster_officer_density',
};

export function parseGridDensity(raw: string | null | undefined): GridDensity {
  return raw && raw in DENSITY_PRESETS ? (raw as GridDensity) : DEFAULT_DENSITY;
}

function load(view: GridView): GridDensity {
  return parseGridDensity(loadPref(STORAGE_KEYS[view]));
}

const current: Record<GridView, GridDensity> = { counter: load('counter'), officer: load('officer') };
const listeners: Record<GridView, Set<(d: GridDensity) => void>> = { counter: new Set(), officer: new Set() };

function setGridDensity(next: GridDensity, view: GridView = 'counter'): void {
  if (next === current[view]) return;
  current[view] = next;
  savePref(STORAGE_KEYS[view], next);
  listeners[view].forEach((l) => l(next));
}

/** The device's density for a grid, live: everything showing it re-lays out when it changes. */
export function useGridDensity(view: GridView = 'counter'): [GridDensity, (d: GridDensity) => void] {
  const [value, setValue] = useState(current[view]);
  useEffect(() => {
    listeners[view].add(setValue);
    setValue(current[view]);
    return () => { listeners[view].delete(setValue); };
  }, [view]);
  return [value, (d) => setGridDensity(d, view)];
}
